import React, { useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import studentApplicationService from '@/shared/services/studentApplicationService';
import { formatDateTime } from '@/components/hiring/reviewerStages';

const FAILED = 'Your resume could not be updated. Try again.';

// Whether the profile holds a resume newer than the one the application
// copied. A copy made before the stamp existed compares by its own date.
export function hasNewerProfileResume(application, profileResume) {
    if (application?.status !== 'submitted') return false;
    if (!application.resume || !profileResume) return false;
    const copied =
        application.resume.copiedFromProfileAt ?? application.resume.uploadedAt;
    return new Date(profileResume.uploadedAt) > new Date(copied);
}

/**
 * Offered on the student's own view of a submitted application while it can
 * still be edited: a newer resume is on the profile, and choosing it makes a
 * new copy for this application. The copy it replaces stays in the history.
 * `onUpdated` gets the updated application.
 */
function NewerResumeOffer({ application, profileResume, onUpdated }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    if (!hasNewerProfileResume(application, profileResume)) return null;
    // When the copy was sent; a copy made before copiedAt existed has only
    // the date its file was uploaded.
    const sentAt = application.resume.copiedAt ?? application.resume.uploadedAt;

    const useNewer = async () => {
        setBusy(true);
        setError('');
        try {
            const updated = await studentApplicationService.useProfileResume(
                application._id
            );
            onUpdated(updated);
        } catch (err) {
            setError(err.response?.data?.message || FAILED);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Alert variant="info" className="mb-3">
                <p className="mb-2">
                    A newer resume is on your profile: {profileResume.fileName},
                    uploaded {formatDateTime(profileResume.uploadedAt)} Mountain
                    time. This application still has{' '}
                    {application.resume.fileName}, sent {formatDateTime(sentAt)}{' '}
                    Mountain time.
                </p>
                <Button
                    variant="primary"
                    size="sm"
                    disabled={busy}
                    onClick={useNewer}
                >
                    {busy ? 'Updating' : 'Use it for this application'}
                </Button>
            </Alert>
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}
        </>
    );
}

export default NewerResumeOffer;
